import axios from "axios";
import { api, throwApiError } from "../../api/api";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type CampaignStatus = "RASCUNHO" | "PUBLICANDO" | "PAUSADA" | "ATIVA" | "ENCERRADA" | "ERRO";
export type BudgetType = "DIARIO" | "TOTAL";
/** ENDERECO: o ponto de uma rua, bairro ou cidade escrito em texto (o raio sai dali). */
export type LocationType = "LOJA" | "CIDADE" | "ENDERECO";
export type ApplicationStatus = "NOVO" | "EM_ANALISE" | "APROVADO" | "REPROVADO";
/** Avaliação da equipe. Sem avaliação = pendente. */
export type ApplicantRating = "BOM" | "MEDIO" | "RUIM";
export type RatingFilter = ApplicantRating | "PENDENTE";
export type ApplicationSort = "recentes" | "antigos" | "nome" | "avaliacao";

export type CampaignQuestion = { id: string; label: string; type: "SIM_NAO" | "TEXTO"; required: boolean };

export type CampaignLocationInput = {
  type: LocationType;
  storeId?: string;
  metaKey?: string;
  label?: string;
  latitude?: number;
  longitude?: number;
  radiusKm: number;
};

export type SaveCampaignPayload = {
  name: string;
  jobTitle: string;
  jobDescription: string;
  questions: CampaignQuestion[];
  resumeRequired: boolean;
  /** Categoria especial Emprego na Meta (padrão: não). Sem ela, loja e lugar aceitam raio a partir de 1 km. */
  employmentCategory?: boolean;
  budgetType: BudgetType;
  budget: number;
  startDate: string;
  endDate: string | null;
  locations: CampaignLocationInput[];
};

export type CampaignListItem = {
  id: string;
  name: string;
  jobTitle: string;
  status: CampaignStatus;
  budgetType: BudgetType;
  budgetCents: number;
  startDate: string;
  endDate: string | null;
  lastError: string | null;
  createdAt: string;
  spendCents: number;
  impressions: number;
  linkClicks: number;
  applications: number;
  costPerApplicationCents: number | null;
};

export type CampaignAd = {
  id: string;
  position: number;
  imageAssetId: string;
  imageFileName: string;
  primaryText: string;
  headline: string;
  description: string | null;
  metaAdId: string | null;
  metaEffectiveStatus: string | null;
  reviewFeedback: string | null;
  applications: number;
};

export type CampaignLocation = {
  id: string;
  type: LocationType;
  storeId: string | null;
  metaKey: string | null;
  label: string;
  latitude: number | null;
  longitude: number | null;
  radiusKm: number;
  storeName: string | null;
};

export type CampaignDetail = {
  id: string;
  name: string;
  slug: string;
  jobTitle: string;
  jobDescription: string;
  questions: CampaignQuestion[];
  resumeRequired: boolean;
  /** Vazio = marcada na categoria Emprego. */
  employmentCategory: boolean | null;
  budgetType: BudgetType;
  budgetCents: number;
  startDate: string;
  endDate: string | null;
  status: CampaignStatus;
  metaStatus: string | null;
  lastError: string | null;
  lastSyncedAt: string | null;
  publishedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  createdBy: { id: string; nomeCompleto: string };
  locations: CampaignLocation[];
  ads: CampaignAd[];
  totals: { spendCents: number; impressions: number; linkClicks: number; applications: number; costPerApplicationCents: number | null };
  applicationUrl: string | null;
  metaEnabled: boolean;
  publishBlocker: string | null;
  /** Textos de anúncio sugeridos pela IA, para usar ao incluir o anúncio. */
  suggestedAds: { angle: string; primaryText: string; headline: string; description: string }[] | null;
};

export type CampaignInsightDay = {
  date: string;
  spendCents: number;
  impressions: number;
  reach: number;
  linkClicks: number;
  applications: number;
};

export type FormSettings = {
  minRadiusKm: number;
  /** Sem a categoria Emprego: o raio mínimo da loja e o da cidade. */
  minRadiusNoCategoryKm: number;
  minCityRadiusKm: number;
  maxDailyBudget: number;
  privacyReady: boolean;
  metaEnabled: boolean;
};
export type CampaignStore = { id: string; name: string; state: string | null; latitude: number; longitude: number };
export type MetaCity = { key: string; name: string; region: string | null };

export type ApplicationListItem = {
  id: string;
  adId: string | null;
  adPosition: number | null;
  fullName: string;
  cpfMasked: string;
  phone: string;
  city: string;
  neighborhood: string;
  status: ApplicationStatus;
  rating: ApplicantRating | null;
  hasResume: boolean;
  createdAt: string;
  campaign: { id: string; name: string };
};

export type ApplicationDetail = {
  id: string;
  campaign: { id: string; name: string; jobTitle: string };
  adPosition: number | null;
  fullName: string;
  cpf: string;
  phone: string;
  email: string | null;
  city: string;
  neighborhood: string;
  answers: { question: string; answer: string }[];
  hasResume: boolean;
  resumeFileName: string | null;
  consentAt: string;
  status: ApplicationStatus;
  notes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  rating: ApplicantRating | null;
  ratedBy: string | null;
  ratedAt: string | null;
  createdAt: string;
};

export type ApplicationsQuery = {
  campaignId?: string;
  /** Separadas por vírgula. */
  status?: string;
  /** Separadas por vírgula: PENDENTE, BOM, MEDIO, RUIM. */
  rating?: string;
  adId?: string;
  q?: string;
  city?: string;
  hasResume?: "true" | "false";
  from?: string;
  to?: string;
  /** JSON `{ [questionId]: "SIM" | "NAO" | texto }`; só com a campanha. */
  answers?: string;
  sort?: ApplicationSort;
  page?: number;
  pageSize?: number;
};

export type ApplicationFacets = {
  ratings: Record<RatingFilter, number>;
  cities: { city: string; count: number }[];
  questions: { id: string; label: string; type: "SIM_NAO" | "TEXTO" }[];
};
export type Paged<T> = { total: number; page: number; pageSize: number; items: T[] };

// ---------------------------------------------------------------------------
// Rótulos e formatação
// ---------------------------------------------------------------------------

export const STATUS_LABEL: Record<CampaignStatus, string> = {
  RASCUNHO: "Rascunho",
  PUBLICANDO: "Publicando",
  PAUSADA: "Pausada",
  ATIVA: "Ativa",
  ENCERRADA: "Encerrada",
  ERRO: "Erro na publicação",
};

export const STATUS_PILL: Record<CampaignStatus, string> = {
  RASCUNHO: "ecoPill ecoPill--off",
  PUBLICANDO: "ecoPill cmpPill--warn",
  PAUSADA: "ecoPill cmpPill--warn",
  ATIVA: "ecoPill",
  ENCERRADA: "ecoPill ecoPill--off",
  ERRO: "ecoPill cmpPill--bad",
};

export const APPLICATION_STATUS_LABEL: Record<ApplicationStatus, string> = {
  NOVO: "Novo",
  EM_ANALISE: "Em análise",
  APROVADO: "Aprovado",
  REPROVADO: "Reprovado",
};

export const RATING_LABEL: Record<RatingFilter, string> = { PENDENTE: "Pendente", BOM: "Bom", MEDIO: "Médio", RUIM: "Ruim" };

export const APPLICATION_STATUS_PILL: Record<ApplicationStatus, string> = {
  NOVO: "ecoPill cmpPill--info",
  EM_ANALISE: "ecoPill cmpPill--warn",
  APROVADO: "ecoPill",
  REPROVADO: "ecoPill ecoPill--off",
};

/** Status do anúncio na Meta, em português. */
export function adStatusLabel(status: string | null): { label: string; tone: "ok" | "warn" | "bad" | "off" } {
  switch (status) {
    case "ACTIVE":
      return { label: "Veiculando", tone: "ok" };
    case "IN_PROCESS":
    case "PENDING_REVIEW":
      return { label: "Em revisão da Meta", tone: "warn" };
    case "DISAPPROVED":
      return { label: "Reprovado pela Meta", tone: "bad" };
    case "WITH_ISSUES":
      return { label: "Com problema", tone: "bad" };
    case "PAUSED":
    case "CAMPAIGN_PAUSED":
    case "ADSET_PAUSED":
      return { label: "Pausado", tone: "off" };
    case "ARCHIVED":
    case "DELETED":
      return { label: "Encerrado", tone: "off" };
    case null:
    case undefined:
      return { label: "Ainda não publicado", tone: "off" };
    default:
      return { label: status, tone: "off" };
  }
}

export const fmtMoney = (cents: number | null | undefined) =>
  cents == null ? "—" : (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const fmtInt = (n: number) => n.toLocaleString("pt-BR");

/** `2026-11-03` → `03/11/2026`, sem fuso (data civil). */
export const fmtDate = (value: string | null | undefined) => {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

export const fmtDateTime = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

/** `5521999998888` → `(21) 99999-8888`. */
export const fmtPhone = (digits: string) => {
  const local = digits.startsWith("55") ? digits.slice(2) : digits;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return digits;
};

export const fmtCpf = (digits: string) =>
  digits.length === 11 ? `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}` : digits;

export const whatsappLink = (digits: string) => `https://wa.me/${digits}`;

export const budgetLabel = (type: BudgetType, cents: number) => `${fmtMoney(cents)} ${type === "DIARIO" ? "por dia" : "no total"}`;

// ---------------------------------------------------------------------------
// Chamadas do staff
// ---------------------------------------------------------------------------

const call = async <T>(fallback: string, fn: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const { data } = await fn();
    return data;
  } catch (error) {
    return throwApiError(error, fallback);
  }
};

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

export const listCampaignsApi = (status?: CampaignStatus) =>
  call<CampaignListItem[]>("Erro ao listar as campanhas.", () => api.get("/campaigns", { params: status ? { status } : undefined }));

export const getFormSettingsApi = () => call<FormSettings>("Erro ao carregar as configurações.", () => api.get("/campaigns/form-settings"));

export const listCampaignStoresApi = () => call<CampaignStore[]>("Erro ao listar as lojas.", () => api.get("/campaigns/stores"));

export const searchCitiesApi = (q: string) =>
  call<MetaCity[]>("Erro ao buscar a cidade na Meta.", () => api.get("/campaigns/meta/cities", { params: { q } }));

export const getCampaignApi = (id: string) => call<CampaignDetail>("Erro ao carregar a campanha.", () => api.get(`/campaigns/${id}`));

export const createCampaignApi = (payload: SaveCampaignPayload) =>
  call<{ id: string }>("Erro ao criar a campanha.", () => api.post("/campaigns", payload));

export const updateCampaignApi = (id: string, payload: SaveCampaignPayload) =>
  call<{ id: string }>("Erro ao salvar a campanha.", () => api.put(`/campaigns/${id}`, payload));

export const deleteCampaignApi = (id: string) => call<{ id: string }>("Erro ao excluir a campanha.", () => api.delete(`/campaigns/${id}`));

export type AdPayload = { primaryText: string; headline: string; description?: string; image?: File | null };

const adForm = (payload: AdPayload) => {
  const form = new FormData();
  form.append("primaryText", payload.primaryText);
  form.append("headline", payload.headline);
  if (payload.description) form.append("description", payload.description);
  if (payload.image) form.append("image", payload.image);
  return form;
};

export const createAdApi = (campaignId: string, payload: AdPayload) =>
  call<{ id: string }>("Erro ao incluir o anúncio.", () => api.post(`/campaigns/${campaignId}/ads`, adForm(payload), MULTIPART));

export const updateAdApi = (campaignId: string, adId: string, payload: AdPayload) =>
  call<{ id: string }>("Erro ao salvar o anúncio.", () => api.put(`/campaigns/${campaignId}/ads/${adId}`, adForm(payload), MULTIPART));

export const deleteAdApi = (campaignId: string, adId: string) =>
  call<{ id: string }>("Erro ao excluir o anúncio.", () => api.delete(`/campaigns/${campaignId}/ads/${adId}`));

export const getAdImageApi = (campaignId: string, adId: string) =>
  call<Blob>("Erro ao carregar a imagem.", () => api.get(`/campaigns/${campaignId}/ads/${adId}/image`, { responseType: "blob" }));

type ActionResult = { id: string; status: CampaignStatus };

export const publishCampaignApi = (id: string) => call<ActionResult>("Erro ao publicar na Meta.", () => api.post(`/campaigns/${id}/publish`));
export const activateCampaignApi = (id: string) => call<ActionResult>("Erro ao ativar a campanha.", () => api.post(`/campaigns/${id}/activate`));
export const pauseCampaignApi = (id: string) => call<ActionResult>("Erro ao pausar a campanha.", () => api.post(`/campaigns/${id}/pause`));
export const endCampaignApi = (id: string) => call<ActionResult>("Erro ao encerrar a campanha.", () => api.post(`/campaigns/${id}/end`));
export const syncCampaignApi = (id: string) =>
  call<{ skipped: boolean; lastSyncedAt: string }>("Erro ao atualizar os resultados.", () => api.post(`/campaigns/${id}/sync`));

export const getInsightsApi = (id: string) =>
  call<{ lastSyncedAt: string | null; days: CampaignInsightDay[] }>("Erro ao carregar os resultados.", () => api.get(`/campaigns/${id}/insights`));

// Candidatos ------------------------------------------------------------------

export const listApplicationsApi = (query: ApplicationsQuery) =>
  call<Paged<ApplicationListItem>>("Erro ao listar os candidatos.", () => api.get("/campaign-applications", { params: query }));

export const getApplicationFacetsApi = (query: ApplicationsQuery) =>
  call<ApplicationFacets>("Erro ao carregar os filtros.", () => api.get("/campaign-applications/facets", { params: query }));

export const getApplicationApi = (id: string) =>
  call<ApplicationDetail>("Erro ao carregar o candidato.", () => api.get(`/campaign-applications/${id}`));

export const updateApplicationApi = (id: string, payload: { status?: ApplicationStatus; notes?: string | null; rating?: ApplicantRating | null }) =>
  call<{ id: string }>("Erro ao salvar o candidato.", () => api.patch(`/campaign-applications/${id}`, payload));

export const downloadResumeApi = (id: string) =>
  call<Blob>("Erro ao baixar o currículo.", () => api.get(`/campaign-applications/${id}/resume`, { responseType: "blob" }));

export const exportApplicationsApi = (query: ApplicationsQuery) =>
  call<Blob>("Erro ao exportar os candidatos.", () => api.get("/campaign-applications/export", { params: query, responseType: "blob" }));

// ---------------------------------------------------------------------------
// Público (sem login)
// ---------------------------------------------------------------------------

export type PublicCampaign = {
  jobTitle: string;
  jobDescription: string;
  locations: string[];
  questions: CampaignQuestion[];
  resumeRequired: boolean;
  state: "ABERTA" | "ENCERRADA" | "INDISPONIVEL";
  consent: { version: string; text: string | null };
  controllerName: string | null;
};

export type PrivacyInfo = { controllerName: string | null; controllerCnpj: string | null; contact: string | null };

/** Erro das rotas públicas com o status, para a tela escolher a mensagem. */
export class PublicError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

const publicCall = async <T>(fallback: string, fn: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const { data } = await fn();
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as any;
      const msg = Array.isArray(data?.message) ? data.message.join(" | ") : typeof data?.message === "string" ? data.message : fallback;
      throw new PublicError(msg, error.response?.status ?? 0);
    }
    throw new PublicError(fallback, 0);
  }
};

export const getPublicCampaignApi = (slug: string) =>
  publicCall<PublicCampaign>("Não foi possível carregar a vaga.", () => api.get(`/public/candidatura/${encodeURIComponent(slug)}`));

export const getPrivacyApi = () => publicCall<PrivacyInfo>("Não foi possível carregar a política.", () => api.get("/public/politica-de-privacidade"));

export type ApplyPayload = {
  fullName: string;
  cpf: string;
  phone: string;
  email: string;
  city: string;
  neighborhood: string;
  answers: Record<string, string>;
  adId: string | null;
  website: string;
  resume: File | null;
};

export const applyApi = (slug: string, p: ApplyPayload) => {
  const form = new FormData();
  form.append("fullName", p.fullName);
  form.append("cpf", p.cpf);
  form.append("phone", p.phone);
  if (p.email.trim()) form.append("email", p.email.trim());
  form.append("city", p.city);
  form.append("neighborhood", p.neighborhood);
  form.append("answers", JSON.stringify(p.answers));
  form.append("consent", "true");
  if (p.adId) form.append("adId", p.adId);
  if (p.website) form.append("website", p.website);
  if (p.resume) form.append("resume", p.resume);
  return publicCall<{ ok: true }>("Não foi possível enviar a candidatura.", () =>
    api.post(`/public/candidatura/${encodeURIComponent(slug)}`, form, MULTIPART),
  );
};

// Prévia oficial da Meta ----------------------------------------------------------

export type PreviewFormat = "MOBILE_FEED_STANDARD" | "INSTAGRAM_STANDARD" | "INSTAGRAM_STORY" | "FACEBOOK_STORY_MOBILE";
export type AdPreview = { format: PreviewFormat; src: string; width: number | null; height: number | null };

export const PREVIEW_FORMATS: { value: PreviewFormat; label: string }[] = [
  { value: "MOBILE_FEED_STANDARD", label: "Facebook" },
  { value: "INSTAGRAM_STANDARD", label: "Instagram" },
  { value: "INSTAGRAM_STORY", label: "Stories Instagram" },
  { value: "FACEBOOK_STORY_MOBILE", label: "Stories Facebook" },
];

export const previewAdApi = (campaignId: string, adId: string, format: PreviewFormat) =>
  call<AdPreview>("Erro ao gerar a prévia na Meta.", () => api.post(`/campaigns/${campaignId}/ads/${adId}/preview`, { format }));

/** Quem assina os anúncios (a Página e o Instagram da conta, lidos da Meta em 31/10/2026). Só para a prévia simulada. */
export const AD_IDENTITY = { pageName: "Eco System Serviços", instagram: "ecosystemservicos2024" };

// Assistente de IA ------------------------------------------------------------

export type AiStatus = { enabled: boolean; spentUsd: number; limitUsd: number; reached: boolean };

export type DraftInterview = {
  role: string;
  storeIds?: string[];
  where?: string;
  peopleNeeded: number;
  deadline: string;
  totalBudget: number;
  offer?: string;
  requirements?: string;
  notes?: string;
};

export type SuggestedAd = { angle: string; primaryText: string; headline: string; description: string };

export type AiSuggestion = {
  explanation: string;
  testPlan: string;
  warnings: string[];
  campaign: {
    name: string;
    jobTitle: string;
    jobDescription: string;
    questions: CampaignQuestion[];
    resumeRequired: boolean;
    employmentCategory?: boolean;
    budgetType: BudgetType;
    budget: number;
    startDate: string;
    endDate: string | null;
    storeLocations: { storeId: string; radiusKm: number; label: string }[];
    cities: { metaKey: string; label: string; radiusKm: number }[];
    /** Lugares escritos em texto, já achados no mapa. */
    points?: { label: string; latitude: number; longitude: number; radiusKm: number }[];
  };
  ads: SuggestedAd[];
  /** Só na "próxima": anúncios desta campanha a reaproveitar, com a imagem. */
  sourceCampaignId?: string;
  reuseAds?: { id: string; position: number; headline: string; primaryText: string }[];
};

export type AiAnalysis = {
  id: string;
  createdAt: string;
  createdBy: string | null;
  verdict: "BOM" | "ATENCAO" | "RUIM" | "CEDO_DEMAIS";
  headline: string;
  summary: string;
  whatWorked: string[];
  whatDidnt: string[];
  actions: { title: string; detail: string; priority: "ALTA" | "MEDIA" | "BAIXA" }[];
  budget: { recommendation: "MANTER" | "AUMENTAR" | "DIMINUIR" | "PAUSAR" | "ENCERRAR"; suggestedDailyReais: number; reason: string };
  bestAdPosition: number;
  glossary: { term: string; meaning: string }[];
};

export type ApplySuggestionPayload = SaveCampaignPayload & {
  /** Rascunho já salvo desta sugestão: atualiza em vez de criar outro. */
  campaignId?: string;
  suggestedAds: SuggestedAd[];
  sourceCampaignId?: string;
  copyAdIds?: string[];
};

export type AiChatMessage = { role: "user" | "assistant"; content: string };

const AI_TIMEOUT = { timeout: 180_000 };

export const getAiStatusApi = () => call<AiStatus>("Erro ao consultar a Eco-IA.", () => api.get("/campaign-ai/status"));

export const draftCampaignAiApi = (payload: DraftInterview) =>
  call<AiSuggestion>("A Eco-IA não conseguiu sugerir a campanha.", () => api.post("/campaign-ai/draft", payload, AI_TIMEOUT));

export const nextCampaignAiApi = (campaignId: string) =>
  call<AiSuggestion>("A Eco-IA não conseguiu sugerir a próxima campanha.", () => api.post(`/campaign-ai/campaigns/${campaignId}/next`, undefined, AI_TIMEOUT));

/** Pede à Eco-IA uma mudança na sugestão que está na tela; volta a sugestão inteira e o que mudou. */
export const refineSuggestionApi = (suggestion: AiSuggestion, instruction: string, history: AiChatMessage[]) =>
  call<{ reply: string; suggestion: AiSuggestion }>("A Eco-IA não conseguiu ajustar a sugestão.", () =>
    api.post("/campaign-ai/refine", { suggestion, instruction, history }, AI_TIMEOUT),
  );

export const applySuggestionApi = (payload: ApplySuggestionPayload) =>
  call<{ id: string; name: string; copiedAds: number }>("Erro ao criar o rascunho.", () => api.post("/campaign-ai/apply", payload));

export const analyzeCampaignAiApi = (campaignId: string) =>
  call<AiAnalysis>("A Eco-IA não conseguiu analisar a campanha.", () => api.post(`/campaign-ai/campaigns/${campaignId}/analyze`, undefined, AI_TIMEOUT));

export const lastAnalysisApi = (campaignId: string) =>
  call<AiAnalysis | null>("Erro ao carregar a análise.", () => api.get(`/campaign-ai/campaigns/${campaignId}/analysis`));

export const chatAiApi = (messages: AiChatMessage[], campaignId?: string) =>
  call<{ answer: string }>("A Eco-IA não respondeu.", () => api.post("/campaign-ai/chat", { messages, ...(campaignId ? { campaignId } : {}) }, AI_TIMEOUT));

export const VERDICT_LABEL: Record<AiAnalysis["verdict"], { label: string; tone: "ok" | "warn" | "bad" | "off" }> = {
  BOM: { label: "Indo bem", tone: "ok" },
  ATENCAO: { label: "Precisa de atenção", tone: "warn" },
  RUIM: { label: "Indo mal", tone: "bad" },
  CEDO_DEMAIS: { label: "Ainda é cedo", tone: "off" },
};

export const BUDGET_ADVICE_LABEL: Record<AiAnalysis["budget"]["recommendation"], string> = {
  MANTER: "Manter o orçamento",
  AUMENTAR: "Aumentar o orçamento",
  DIMINUIR: "Diminuir o orçamento",
  PAUSAR: "Pausar a campanha",
  ENCERRAR: "Encerrar a campanha",
};

/** Sugestão revista → corpo do "criar rascunho". */
export function suggestionToPayload(s: AiSuggestion, keepReuseIds: string[]): ApplySuggestionPayload {
  return {
    name: s.campaign.name,
    jobTitle: s.campaign.jobTitle,
    jobDescription: s.campaign.jobDescription,
    questions: s.campaign.questions,
    resumeRequired: s.campaign.resumeRequired,
    employmentCategory: s.campaign.employmentCategory === true,
    budgetType: s.campaign.budgetType,
    budget: s.campaign.budget,
    startDate: s.campaign.startDate,
    endDate: s.campaign.endDate,
    locations: [
      ...s.campaign.storeLocations.map((l) => ({ type: "LOJA" as const, storeId: l.storeId, radiusKm: l.radiusKm })),
      ...s.campaign.cities.map((c) => ({ type: "CIDADE" as const, metaKey: c.metaKey, label: c.label, radiusKm: c.radiusKm })),
      ...(s.campaign.points ?? []).map((pt) => ({
        type: "ENDERECO" as const,
        label: pt.label,
        latitude: pt.latitude,
        longitude: pt.longitude,
        radiusKm: pt.radiusKm,
      })),
    ],
    suggestedAds: s.ads,
    ...(s.sourceCampaignId && keepReuseIds.length ? { sourceCampaignId: s.sourceCampaignId, copyAdIds: keepReuseIds } : {}),
  };
}

/** O rascunho salvo no formato da sugestão, para continuar ajustando com a Eco-IA. */
export function campaignToSuggestion(c: CampaignDetail): AiSuggestion {
  return {
    explanation: "",
    testPlan: "",
    warnings: [],
    campaign: {
      name: c.name,
      jobTitle: c.jobTitle,
      jobDescription: c.jobDescription,
      questions: c.questions ?? [],
      resumeRequired: c.resumeRequired,
      employmentCategory: c.employmentCategory === true,
      budgetType: c.budgetType,
      budget: c.budgetCents / 100,
      startDate: c.startDate.slice(0, 10),
      endDate: c.endDate ? c.endDate.slice(0, 10) : null,
      storeLocations: c.locations
        .filter((l) => l.type === "LOJA" && l.storeId)
        .map((l) => ({ storeId: l.storeId!, radiusKm: l.radiusKm, label: l.storeName ?? l.label })),
      cities: c.locations.filter((l) => l.type === "CIDADE" && l.metaKey).map((l) => ({ metaKey: l.metaKey!, label: l.label, radiusKm: l.radiusKm })),
      points: c.locations
        .filter((l) => l.type === "ENDERECO" && l.latitude != null && l.longitude != null)
        .map((l) => ({ label: l.label, latitude: l.latitude!, longitude: l.longitude!, radiusKm: l.radiusKm })),
    },
    ads: (c.suggestedAds ?? []).map((a) => ({ angle: a.angle ?? "", primaryText: a.primaryText, headline: a.headline, description: a.description ?? "" })),
  };
}
